import { ReactNode, useRef, useState } from "react"
import ListPager, { LIST_PAGE_SIZE } from '@client/components/ListPager'
import { useTranslation } from "react-i18next"
import { SortType } from 'rsuite/esm/Table'
import { Checkbox } from 'rsuite'
import useQuery from "@client/utils/useQuery"
import tokenService from '@client/utils/tokenService'
import { PanelError } from "@client/components/ErrorProvider"
import { Table } from "@client/components/Controls"
import ModalDialog from "@client/components/ModalDialog"
import Loading from "@/client/components/Loading"
import { useLayout } from "@client/components/Layout/LayoutManager"
import ProjectModel, { TProject } from "@/client/models/project"
import { user as authUser } from '@client/stores/authStore'
import { setSettings, getSettings } from '@client/stores/settingsStore'
import project from '@client/stores/projectStore'
import UserModel from "@/client/models/user"

const projectModel = new ProjectModel()
const userModel = new UserModel()

export default function ProjectList() {
    const { t } = useTranslation()
    const { setOpenProject } = useLayout()
    const refreshCounter = useRef(0)
    const [page, setPage] = useState<number>(1)
    const [sortColumn, setSortColumn] = useState<string>('name');
    const [sortType, setSortType] = useState<SortType | undefined>('asc');
    const [selectedProject, setSelectedProject] = useState<TProject | null>(null)
    // the archive is opened for reading and export, behind its own switch
    const [showArchive, setShowArchive] = useState<boolean>(false)
    const { loading, error, data } = useQuery(projectModel.list, {
        args: { page: page, count: LIST_PAGE_SIZE, sortField: sortColumn, sortOrder: sortType || 'asc', archived: showArchive ? '1' : undefined },
        refreshCounter: refreshCounter.current
    });
    const canSort = data?.rows && data.rows.length > 0
    const buttons = [
        { status: 'open', label: t('Open'), disabled: !selectedProject },
        { status: 'cancel', label: t('Cancel') },
    ]

    if (error) return <PanelError message={error.message} />
    if (loading) return <Loading />

    function handleUserSettings(prj: TProject) {
        const user = authUser.value
        setSettings(user._id, 'openProject', prj._id)
        user.settings = getSettings(user._id)
        tokenService.updateLocalUser(user)
        userModel.updateSettings(user._id, user.settings)
    }

    function handleDialogButtons(status: string) {
        if (status === 'cancel') {
            setOpenProject(false)
            return true
        }

        if (status === 'open') {
            if (!selectedProject) {
                project.clearProject()
                return false
            }
            project.loadProject(selectedProject)
            handleUserSettings(selectedProject)
        }

        setOpenProject(false)
        return true
    }

    function onSort(column: string, sortOrder?: SortType) {
        setPage(1)
        setSortColumn(column);
        setSortType(sortOrder);
        refreshCounter.current++
    }

    function onSelectProject(row: TProject): void {
        setSelectedProject(row)
    }

    function onCloseDialog() {
        if (selectedProject) {
            project.loadProject(selectedProject)
            handleUserSettings(selectedProject)
        }
        setOpenProject(false)
    }

    function onRenderEmpty() {
        return (
            <div className="rs-table-body-info">{t('No projects found.')}</div>
        )
    }

    function onRenderRow(children?: ReactNode, rowData?: any): ReactNode {
        if (rowData && rowData == selectedProject) {
            return <div className="active">{children}</div>
        }

        return children
    }

    return (
        <ModalDialog
            id="project-list-dialog"
            title={t('Open project')}
            buttons={buttons}
            onClick={handleDialogButtons}
            size="md"
            fullHeight
        >
            {/* Above the table, not under it: the table fills the dialog's
              * height, and whatever came after it was cut off — the pager
              * could not be reached, nor could the archive switch. */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1em', marginBottom: '0.5em' }}>
                <Checkbox
                    checked={showArchive}
                    onChange={(_value, checked) => {
                        refreshCounter.current++
                        setPage(1)
                        setSelectedProject(null)
                        setShowArchive(checked)
                    }}
                >
                    {t('Archive')}
                </Checkbox>
                {/* the dialog loaded one page and offered no way to the next */}
                <ListPager total={Number(data?.total ?? 0)} page={page} onChangePage={(p) => { refreshCounter.current++; setPage(p) }} disabled={loading} />
            </div>
            {/* keyed by page and switch: a new page started where the last one
              * was scrolled to, with its first rows hidden above the view */}
            <Table
                key={`${page}-${showArchive}`}
                className="tablist-table"
                loading={loading}
                wordWrap="break-word"
                fillHeight
                sortColumn={sortColumn}
                sortType={sortType}
                onSortColumn={onSort}
                showHeader
                bordered
                data={data.rows}
                renderEmpty={onRenderEmpty}
                renderRow={onRenderRow}
                onRowClick={onSelectProject}
                onDoubleClick={onCloseDialog}
            >
                <Table.Column align="left" sortable={canSort} flexGrow={1} >
                    <Table.HeaderCell className="name" flexGrow={1}>
                        {t('Project name')}
                    </Table.HeaderCell>
                    <Table.Cell className="name" dataKey="name" />
                </Table.Column>
            </Table >
        </ModalDialog>
    )
}